/**
 * Encrypted Storage Wrapper
 *
 * Provides a StateStorage-compatible interface for Zustand's persist middleware
 * that encrypts/decrypts data before writing to/reading from localStorage.
 *
 * Uses XOR cipher with base64 encoding for obfuscation of sensitive data
 * (tokens, user info) stored in localStorage. This is NOT cryptographic-grade
 * encryption but prevents casual inspection of stored auth data.
 *
 * For production, consider using Web Crypto API or a library like crypto-js.
 */

import type { StateStorage } from 'zustand/middleware';

/**
 * Encryption key derived from a static secret.
 * In production, this could be derived from a user-specific value or environment variable.
 */
const ENCRYPTION_KEY = 'mellify-store-enc-2024';

/**
 * XOR-based string obfuscation.
 * Applies XOR operation between the input string and the key, character by character.
 */
function xorCipher(input: string, key: string): string {
  let result = '';
  for (let i = 0; i < input.length; i++) {
    result += String.fromCharCode(input.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return result;
}

/**
 * Encode a string to base64 (browser-safe).
 */
function toBase64(str: string): string {
  try {
    // Handle Unicode characters
    return btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
        String.fromCharCode(parseInt(p1, 16))
      )
    );
  } catch {
    return btoa(str);
  }
}

/**
 * Decode a base64 string (browser-safe).
 */
function fromBase64(str: string): string {
  try {
    return decodeURIComponent(
      Array.from(atob(str))
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch {
    return atob(str);
  }
}

/**
 * Encrypt a string value for storage.
 */
export function encryptValue(value: string): string {
  const xored = xorCipher(value, ENCRYPTION_KEY);
  return toBase64(xored);
}

/**
 * Decrypt a stored string value.
 */
export function decryptValue(encrypted: string): string {
  const decoded = fromBase64(encrypted);
  return xorCipher(decoded, ENCRYPTION_KEY);
}

/**
 * Creates an encrypted StateStorage implementation for Zustand's persist middleware.
 *
 * @param prefix - Optional prefix for localStorage keys (useful for namespacing)
 * @returns StateStorage-compatible object with encrypted getItem/setItem/removeItem
 *
 * @example
 * ```ts
 * import { createEncryptedStorage } from '../lib/storage';
 *
 * const authStore = create(
 *   persist(storeDefinition, {
 *     name: 'mellify-auth',
 *     storage: createEncryptedStorage(),
 *   })
 * );
 * ```
 */
export function createEncryptedStorage(prefix?: string): StateStorage {
  const getKey = (name: string) => (prefix ? `${prefix}:${name}` : name);

  return {
    getItem(name: string): string | null {
      try {
        const key = getKey(name);
        const raw = localStorage.getItem(key);
        if (raw === null) return null;
        return decryptValue(raw);
      } catch (error) {
        console.warn(`[EncryptedStorage] Failed to read key "${name}":`, error);
        // If decryption fails (e.g., corrupted data), remove the item
        try {
          localStorage.removeItem(getKey(name));
        } catch {
          // Ignore cleanup errors
        }
        return null;
      }
    },

    setItem(name: string, value: string): void {
      try {
        const key = getKey(name);
        const encrypted = encryptValue(value);
        localStorage.setItem(key, encrypted);
      } catch (error) {
        console.warn(`[EncryptedStorage] Failed to write key "${name}":`, error);
      }
    },

    removeItem(name: string): void {
      try {
        const key = getKey(name);
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`[EncryptedStorage] Failed to remove key "${name}":`, error);
      }
    },
  };
}

/**
 * Standard (non-encrypted) localStorage wrapper for Zustand persist.
 * Used for non-sensitive store data (UI preferences, etc.).
 */
export const localStorageWrapper: StateStorage = {
  getItem(name: string): string | null {
    try {
      return localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem(name: string, value: string): void {
    try {
      localStorage.setItem(name, value);
    } catch {
      // Ignore quota errors
    }
  },
  removeItem(name: string): void {
    try {
      localStorage.removeItem(name);
    } catch {
      // Ignore
    }
  },
};